import type React from 'react';
import type { ReactNode } from 'react';
import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@/analytics';
import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import { Spinner } from '@/components/atoms/Spinner';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import type { CageType } from '@/domain/cages/types';
import { translateDataLabel } from '@/i18n/dataLabels';
import type { ClientLocationProps } from '@/routes/Internal/routes/Client/Client';
import { allowedCageTypes, isSmoltLocation } from '@/utils/locationTypeHelpers';

import styles from './EditCageModal.module.scss';

export interface EditableCage {
    id: number;
    name: string;
    cageTypes?: { id: number; name: string } | null;
    fishTypes?: { id: number; name: string } | null;
}

export interface EditCageModalProps {
    /** The pen being edited. The modal is open while this is set. */
    cage?: EditableCage;
    location?: ClientLocationProps;
    cageTypesList?: CageType[];
    /**
     * Selectable species. Structural on purpose, matching AddCageModal: only id and name are
     * needed, so the modal takes no dependency on the domain or generated fish type shapes.
     */
    fishTypesList?: { id: number; name: string }[];
    onClose: () => void;
    updateCage: (
        cageId: number,
        input: { name: string; cageTypeId: number; fishTypeId: number }
    ) => Promise<unknown>;
}

interface FormState {
    name: string;
    nameError: string;
    cageTypeId: number;
    cageTypeError: string;
    fishTypeId: number;
    fishTypeError: string;
    saving: boolean;
    systemError: ReactNode;
}

function makeInitialState(
    cage: EditableCage | undefined,
    location?: ClientLocationProps
): FormState {
    return {
        name: cage?.name ?? '',
        nameError: '',
        cageTypeId: cage?.cageTypes?.id ?? -1,
        cageTypeError: '',
        // A pen with no species of its own inherits the location's, which is what the backend
        // would have applied on create. Preselecting it keeps the common case one click, and
        // the hint below the field says where the value came from.
        fishTypeId: cage?.fishTypes?.id ?? location?.fishtypeid ?? -1,
        fishTypeError: '',
        saving: false,
        systemError: '',
    };
}

/**
 * Change a pen's name, type and species from the internal client overview.
 *
 * Species belongs to the pen (WB-2207), so it has to be correctable where pens are managed
 * rather than only on the cage settings page. Name and cage type come along because
 * `UpdateCageDto` requires both on every update, so the modal has to carry them regardless.
 */
export default function EditCageModal(props: EditCageModalProps) {
    const { cage, location, cageTypesList = [], fishTypesList = [], onClose, updateCage } = props;

    const { t } = useTranslation('clientLocations');
    const { t: tCommon } = useTranslation('common');
    // Cage and fish type names arrive from the DB as translation keys. Legacy, shared with
    // AddCageModal.
    const { t: tLegacy } = useTranslation();

    const [formState, setFormState] = useState<FormState>(() => makeInitialState(cage, location));

    /* Same colon strip as AddCageModal: React 18's useId returns ":r45:", which is legal in
       an `id` attribute but not in a CSS selector. */
    const nameInputId = useId().replace(/:/g, '');
    const cageTypeSelectId = useId().replace(/:/g, '');
    const fishTypeSelectId = useId().replace(/:/g, '');

    const isLocked = isSmoltLocation(location?.location_type);
    const allowed = allowedCageTypes(cageTypesList, location?.location_type);
    // A pen may already hold a type the location type no longer allows. Dropping it from the
    // options would leave the select showing someone else's value and silently rewrite the
    // pen's type on save, so the current type always stays selectable.
    const current = cage?.cageTypes;
    const visibleCageTypes =
        current && !allowed.some((cageType) => cageType.id === current.id)
            ? [...allowed, { id: current.id, name: current.name }]
            : allowed;

    const inheritsLocationSpecies = !cage?.fishTypes && location?.fishtypeid !== undefined;

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleSubmit = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();

            if (!cage) return;

            const name = formState.name.trim();
            if (name.length === 0) {
                setFormState((prev) => ({ ...prev, nameError: t('editCageModal.nameRequired') }));
                return;
            }

            if (formState.cageTypeId === -1) {
                setFormState((prev) => ({
                    ...prev,
                    cageTypeError: t('editCageModal.chooseCageType'),
                }));
                return;
            }

            if (formState.fishTypeId === -1) {
                setFormState((prev) => ({
                    ...prev,
                    fishTypeError: t('editCageModal.chooseFishType'),
                }));
                return;
            }

            setFormState((prev) => ({ ...prev, saving: true, systemError: '' }));

            try {
                await updateCage(cage.id, {
                    name,
                    cageTypeId: formState.cageTypeId,
                    fishTypeId: formState.fishTypeId,
                });
                analytics.featureActionUsed({ featureName: 'editing_pen' });
                onClose();
            } catch (error: unknown) {
                const systemError = apiErrorContent(error);
                setFormState((prev) => ({ ...prev, saving: false, systemError }));
            }
        },
        [cage, formState, onClose, t, updateCage]
    );

    return (
        <ModalComponent
            isOpen={cage !== undefined}
            onClose={handleClose}
            centered
            title={t('editCageModal.title')}>
            <ModalBody>
                <FormField
                    label={t('editCageModal.name')}
                    htmlFor={nameInputId}
                    error={formState.nameError}>
                    <TextField
                        id={nameInputId}
                        type="text"
                        name="name"
                        value={formState.name}
                        onChange={(e) =>
                            setFormState((prev) => ({
                                ...prev,
                                name: e.target.value,
                                nameError: '',
                            }))
                        }
                        invalid={!!formState.nameError}
                    />
                </FormField>

                <FormField
                    label={t('editCageModal.cageType')}
                    htmlFor={cageTypeSelectId}
                    error={formState.cageTypeError}>
                    <Select
                        id={cageTypeSelectId}
                        name="cageType"
                        value={formState.cageTypeId}
                        disabled={isLocked}
                        onChange={(e) =>
                            setFormState((prev) => ({
                                ...prev,
                                cageTypeId: Number.parseInt(e.target.value, 10),
                                cageTypeError: '',
                            }))
                        }
                        invalid={!!formState.cageTypeError}>
                        <option value={-1}>{`--- ${t('editCageModal.chooseCageType')} ---`}</option>
                        {visibleCageTypes.map((cageType) => (
                            <option key={cageType.id} value={cageType.id}>
                                {translateDataLabel(tLegacy, 'cageTypes', cageType.name)}
                            </option>
                        ))}
                    </Select>
                </FormField>

                <FormField
                    label={t('editCageModal.fishType')}
                    htmlFor={fishTypeSelectId}
                    error={formState.fishTypeError}
                    hint={
                        !formState.fishTypeError && inheritsLocationSpecies
                            ? t('editCageModal.inheritedFishType')
                            : undefined
                    }>
                    <Select
                        id={fishTypeSelectId}
                        name="fishType"
                        value={formState.fishTypeId}
                        onChange={(e) =>
                            setFormState((prev) => ({
                                ...prev,
                                fishTypeId: Number.parseInt(e.target.value, 10),
                                fishTypeError: '',
                            }))
                        }
                        invalid={!!formState.fishTypeError}>
                        <option value={-1}>{`--- ${t('editCageModal.chooseFishType')} ---`}</option>
                        {fishTypesList.map((fishType) => (
                            <option key={fishType.id} value={fishType.id}>
                                {translateDataLabel(tLegacy, 'fishTypes', fishType.name)}
                            </option>
                        ))}
                    </Select>
                </FormField>

                {formState.systemError && <Alert tone="danger">{formState.systemError}</Alert>}
            </ModalBody>
            <ModalFooter>
                {formState.saving && (
                    <div className={styles.saving}>
                        <Spinner size="md" />
                    </div>
                )}
                <Button variant="secondary" onClick={handleClose}>
                    {tCommon('actions.cancel')}
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={formState.saving}>
                    {tCommon('actions.save')}
                </Button>
            </ModalFooter>
        </ModalComponent>
    );
}
